import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  CloneQuoteData,
  getDefaultQuoteDataQuery,
  PrepareQuoteDataForWorker,
  type QuoteData,
  QuoteType,
  removeProps,
} from '@iqd/shared-quoting/core';
import { GENERATE_BUDGET_QUOTE, type GenerateBudgetQuoteResult } from './graphql';

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export interface ContactDetails {
  email: string;
  phoneNumber: string;
}

export interface DomainContext {
  embeddingDomain: string;
  isEmbedded: boolean;
  referrerUrl: string;
}

export interface BudgetGenerationResult {
  drawing: unknown;
  backsheet: unknown;
  quoteData?: unknown;
  infoMessage?: string;
}

export interface UseBudgetSession {
  quoteData: QuoteData | null;
  drawing: unknown;
  backsheet: unknown;
  generating: boolean;
  setQuoteData: (qd: QuoteData) => void;
  reset: () => Promise<void>;
  /**
   * Generates the budget quote with the customer's contact details attached in
   * the same call. Returns the result on success, or null if the API returned
   * nothing.
   */
  generateAndAttachContact: (
    quoteData: QuoteData,
    contact: ContactDetails,
    domain: DomainContext,
  ) => Promise<BudgetGenerationResult | null>;
}

export function useBudgetSession(): UseBudgetSession {
  const apollo = useApolloClient();
  const [quoteData, setQuoteDataState] = useState<QuoteData | null>(null);
  const [drawing, setDrawing] = useState<unknown>(null);
  const [backsheet, setBacksheet] = useState<unknown>(null);
  const [sessionUuid, setSessionUuid] = useState<string>(() => newSessionId());

  const [generateMutation, { loading: generating }] =
    useMutation<GenerateBudgetQuoteResult>(GENERATE_BUDGET_QUOTE);

  const reset = useCallback(async () => {
    const { data } = await apollo.query({
      query: getDefaultQuoteDataQuery,
      variables: { quoteType: QuoteType.Budget },
      // A template to clone and edit, not an entity the cache can identify.
      fetchPolicy: 'no-cache',
    });
    const defaultQd = removeProps(
      (data as { defaultQuoteData: QuoteData }).defaultQuoteData,
      '__typename',
    );
    setQuoteDataState(CloneQuoteData(defaultQd));
    setDrawing(null);
    setBacksheet(null);
    setSessionUuid(newSessionId());
  }, [apollo]);

  useEffect(() => {
    if (!quoteData) {
      // The wizard shows its spinner until this answers. A rejection here used
      // to be unhandled, and left the spinner up with nothing in the console.
      reset().catch((error) => console.error('Could not start a budget quote', error));
    }
  }, [quoteData, reset]);

  const setQuoteData = useCallback((qd: QuoteData) => setQuoteDataState(qd), []);

  const generateAndAttachContact = useCallback(
    async (
      qd: QuoteData,
      contact: ContactDetails,
      domain: DomainContext,
    ): Promise<BudgetGenerationResult | null> => {
      setQuoteDataState(qd);

      const generated = await generateMutation({
        variables: {
          sessionData: { username: 'guest', sessionId: sessionUuid },
          quoteData: PrepareQuoteDataForWorker(qd),
          contactDetails: {
            phoneNumber: contact.phoneNumber,
            emailAddress: contact.email,
            ...domain,
          },
        },
      });
      const payload = generated.data?.generateBudgetQuote;
      if (payload?.warnings && payload.warnings.length > 0) {
        throw new Error(payload.warnings.join('\n'));
      }
      if (payload?.errors && payload.errors.length > 0) {
        throw new Error(payload.errors.join('\n'));
      }
      if (!payload?.result) return null;

      setBacksheet(payload.result.backsheet);
      setDrawing(payload.result.drawing);
      if (payload.result.quoteData) {
        setQuoteDataState(payload.result.quoteData as QuoteData);
      }

      return payload.result;
    },
    [sessionUuid, generateMutation],
  );

  return useMemo(
    () => ({
      quoteData,
      drawing,
      backsheet,
      generating,
      setQuoteData,
      reset,
      generateAndAttachContact,
    }),
    [quoteData, drawing, backsheet, generating, setQuoteData, reset, generateAndAttachContact],
  );
}
